import React, { useContext } from 'react'
import moment from 'moment'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { IconButton, Stack } from '@mui/material'
import { Visibility } from '@mui/icons-material'
import { PTSDutiesContext } from '.'

const shiftIsCovered = (shift) =>
  !!shift?.assignedTo ||
  !!shift?.assignedSubcontractor ||
  !!shift?.assignedToSubcontractor

const getShiftAssigneeName = (shift) => {
  if (shift?.assignedTo) {
    return `${shift.assignedTo.userAccount?.firstName ?? ''} ${
      shift.assignedTo.userAccount?.lastName ?? ''
    }`.trim()
  }

  if (shift?.assignedSubcontractor) {
    return `${shift.assignedSubcontractor.userAccount?.firstName ?? ''} ${
      shift.assignedSubcontractor.userAccount?.lastName ?? ''
    }`.trim()
  }

  if (shift?.assignedToSubcontractor) return 'Subcontractor'

  return 'Vacant shift'
}

const AssignShiftComponent = ({ shift, onClick, buttonText }) => {
  const { setViewShift } = useContext(PTSDutiesContext)
  const covered = shiftIsCovered(shift)

  return (
    <Box
      sx={{
        p: 1.4,
        mb: 1,
        borderRadius: 2,
        bgcolor: 'background.paper',

        // Subtle separation from parent card
        border: '0 1px 1px 0',
        borderWidth: 'solid',
        borderColor: 'divider',

        // Modern soft shadow
        boxShadow: '0 2px 6px rgba(0,0,0,0.06)',

        // Nice interaction
        transition: 'all .15s ease',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        },

        // Accent strip
        position: 'relative',
        '&:before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          borderRadius: 4,
          bgcolor: covered ? 'success.main' : 'warning.main',
        },
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
      >
        <Stack>
          {/* TIME – the hero */}
          <Typography variant="subtitle1" fontWeight={700}>
            {moment(shift.start).format('HH:mm')} →{' '}
            {moment(shift.end).format('HH:mm')}
          </Typography>

          {/* Person */}
          <Typography
            variant="body2"
            color={covered ? 'anBlue.main' : 'text.secondary'}
            fontWeight={600}
          >
            {getShiftAssigneeName(shift)}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={0.5}>
          {/* Duration pill */}
          <Box
            sx={{
              px: 1,
              py: 0.3,
              borderRadius: 10,
              bgcolor: 'action.hover',
            }}
          >
            <Typography variant="caption" fontWeight={600}>
              {moment(shift.end).diff(moment(shift.start), 'hours', true)}h
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={() => setViewShift(shift)}
            sx={{ bgcolor: 'action.hover' }}
          >
            <Visibility fontSize="small" color="primary" />
          </IconButton>
        </Stack>
      </Stack>

      <Button
        variant="contained"
        color="anBlue"
        size="small"
        fullWidth
        sx={{
          mt: 1.2,
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 1.5,
        }}
        onClick={onClick}
      >
        {buttonText ?? 'Assign to Duty'}
      </Button>
    </Box>
  )
}

export default AssignShiftComponent
